import { useState } from 'react';
import type { FlaggedItem, ScanFlag } from '@/types/manifest';
import { formatToESTShort } from '@/lib/utils/date-format';

interface FlaggedItemsTableProps {
  items: FlaggedItem[];
}

const flagStyles: Record<ScanFlag, string> = {
  None: 'bg-gray-500/20 text-gray-400',
  Duplicate: 'bg-yellow-500/20 text-yellow-400',
  Unmanifested: 'bg-red-500/20 text-red-400',
  Manual: 'bg-purple-500/20 text-purple-400',
};

export function FlaggedItemsTable({ items }: FlaggedItemsTableProps) {
  const [filter, setFilter] = useState<ScanFlag | 'all'>('all');

  const filteredItems = items.filter((item) => {
    if (filter === 'all') return true;
    // Manual matches the entry METHOD as well as the flag, so an off-manifest type-in (Manual +
    // Unmanifested) is findable here too. Filtering on flag alone hid exactly the rows that were
    // hardest to spot, since their flag column reads "Unmanifested".
    if (filter === 'Manual') return item.flag === 'Manual' || item.scanType === 'Manual';
    return item.flag === filter;
  });

  const flagFilters: (ScanFlag | 'all')[] = ['all', 'Duplicate', 'Unmanifested', 'Manual'];

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Flagged Items</h3>
        <div className="flex gap-2">
          {flagFilters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Barcode</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Flag</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Operator</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Timestamp (EST)</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  No flagged items
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-700 last:border-b-0">
                  <td className="px-4 py-3 font-mono">
                    <span className="inline-flex items-center gap-2">
                      {item.barcodeValue}
                      {/* Entry method beside the flag, so a hand-keyed off-manifest row reports both
                          facts on one line instead of only "Unmanifested". */}
                      {item.scanType === 'Manual' && item.flag !== 'Manual' && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400"
                          title="Entered manually"
                        >
                          TYPED
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${flagStyles[item.flag]}`}>
                      {item.flag}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{item.operatorName}</td>
                  <td className="px-4 py-3 text-gray-300">
                    {formatToESTShort(item.timestamp)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
